import { Component, OnDestroy, OnInit, ViewEncapsulation, EventEmitter } from '@angular/core';
import { BehaviorSubject, combineLatest, concat, from, lastValueFrom, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OrderComponent } from '../order/order.component';
import { IWorklistItemDto, StatusType, UrgencyType, WorklistItemDataSourceType } from '../api-module/control-centre-api-client';
import { DateTime, Interval } from 'luxon';
import { SettingsService } from '../services/settings.service';
import { distinctUntilChanged, finalize, map, shareReplay, startWith, switchMap, take, tap, takeUntil } from 'rxjs/operators';
import { WorklistService } from './worklist.service';
import { HubConnection } from '@microsoft/signalr';
import { Constants } from '../models/constants';
import { MessageBoxService } from '../messagebox/services/messagebox.service';
import { MessageBoxButtons, MessageBoxIcon } from '../messagebox/messagebox.model';
import equal from 'fast-deep-equal/es6';
import { FeaturesService } from '../services/features.service';
import { OrgSiteSelectionService } from '../shared/org-site-selection/org-site-selection.service';
import { ModalityType } from '@app/shared/model/shared.model';
import { dispatch, Store } from '@ngxs/store';
import * as WorklistFilterActions from './worklist-filters/worklist-filters.actions';
import { WorklistFiltersState } from './worklist-filters/worklist-filters.state';

@Component({
    selector: 'app-worklist',
    templateUrl: './worklist.component.html',
    styleUrls: ['./worklist.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class WorklistComponent implements OnInit, OnDestroy {
    constructor(
        public dialog: MatDialog,
        private readonly settingsService: SettingsService,
        private readonly worklistService: WorklistService,
        private readonly featuresService: FeaturesService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly messageBoxService: MessageBoxService,
        private readonly store: Store
    ) {}

    worklistItems$: Observable<IWorklistItemDto[]>;

    // Tracks when the component is destroyed, to handle unsubscribing to timers
    private readonly destroy$ = new Subject();
    subscriptions = new Subscription();

    hub: HubConnection;

    loading = true;

    selectedItem$: Observable<IWorklistItemDto>;

    clearSearch$ = new EventEmitter<void>();

    defaultCountryCode$: Observable<string>;
    isOrderFormEnabled$: Observable<boolean>;

    timeRangeFilter$: Observable<Interval> = this.store.select(WorklistFiltersState.getTimeRangeFilter);

    filtersVisibleSource = new BehaviorSubject<boolean>(false);
    filtersVisible$ = this.filtersVisibleSource.asObservable();

    filtersApplied$: Observable<boolean> = this.store.select(WorklistFiltersState.filtersApplied);

    filteredKeywordsSource = new Subject<string>();

    private readonly _selectedItemSource = new BehaviorSubject<IWorklistItemDto>(null);

    private readonly _upsertWorklistItemTrigger = new Subject<IWorklistItemDto>();

    private readonly _deleteWorklistItemTrigger = new Subject<string>();

    private readonly _filterRefreshTrigger = new BehaviorSubject(null);

    private readonly _onHoldRecentlyTimers = new Map<string, Subscription>();
    private readonly _updatedItemsMap = new BehaviorSubject<Map<string, IWorklistItemDto>>(null);

    filterCancelledOrders = (items: IWorklistItemDto[]): IWorklistItemDto[] => {
        if (items == null) {
            return items;
        }
        return items.filter(
            item =>
                !(
                    item.dataSource === WorklistItemDataSourceType.Order &&
                    item.status === StatusType.Cancelled &&
                    item.lastUpdatedAt &&
                    DateTime.fromISO(item.lastUpdatedAt) < DateTime.now().minus({ hours: 1 })
                )
        );
    };

    filterWorklistItemsByTimeRange = (items: IWorklistItemDto[]): Observable<IWorklistItemDto[]> => {
        const timeRangeFilter = this.store.select(WorklistFiltersState.getTimeRangeFilter);
        return timeRangeFilter.pipe(
            map(timeRange => {
                if (items == null) {
                    return items;
                }

                return items.filter(item => {
                    switch (item.dataSource) {
                        case WorklistItemDataSourceType.Order:
                            return (
                                timeRange.start <= DateTime.fromISO(item.studyDateTime || DateTime.now().toISO()) &&
                                timeRange.end >= DateTime.fromISO(item.studyDateTime || DateTime.now().toISO())
                            );

                        case WorklistItemDataSourceType.UnlinkedOrder:
                            return (
                                DateTime.fromISO(item.createdAt || DateTime.now().toISO()) >= timeRange.start &&
                                DateTime.fromISO(item.createdAt || DateTime.now().toISO()) <= timeRange.end
                            );

                        default:
                            return true;
                    }
                });
            })
        );
    };

    filterWorklistItemsByUrgencyStatusAndModalityType = (items: IWorklistItemDto[]): Observable<IWorklistItemDto[]> => {
        const latestFilters = combineLatest([
            this.store.select(WorklistFiltersState.getFilteredModalities),
            this.store.select(WorklistFiltersState.getFilteredUrgencies),
            this.store.select(WorklistFiltersState.getFilteredStatuses)
        ]);

        return latestFilters.pipe(
            map(([filteredModalityTypes, filteredUrgencyTypes, filteredStatusTypes]) => {
                if (items == null) {
                    return items;
                }

                const filteredItems = items.filter(item => {
                    if (
                        filteredModalityTypes.length > 0 &&
                        !(
                            filteredModalityTypes.includes(item.modality as ModalityType) ||
                            filteredModalityTypes.includes(ModalityType.Other) ||
                            (filteredModalityTypes.includes(ModalityType.PlainFilm) && ['CR', 'DX'].includes(item.modality))
                        )
                    ) {
                        return false;
                    }
                    if (filteredUrgencyTypes.length > 0) {
                        if (
                            filteredUrgencyTypes.indexOf(item.urgency) < 0 &&
                            !(
                                (filteredUrgencyTypes.includes(UrgencyType.Trauma) || filteredUrgencyTypes.includes(UrgencyType.Stroke)) &&
                                item.urgency === UrgencyType.TraumaAndStroke
                            )
                        ) {
                            return false;
                        }
                    }
                    return !(filteredStatusTypes.length > 0 && filteredStatusTypes.indexOf(item.status) < 0);
                });

                if (
                    this._selectedItemSource.value &&
                    !filteredItems.find(item => item.accessionNumber === this._selectedItemSource.value.accessionNumber)
                ) {
                    filteredItems.unshift(this._selectedItemSource.value);
                }
                return filteredItems;
            })
        );
    };

    filterMergedUnlinkedOrder = (items: IWorklistItemDto[]): IWorklistItemDto[] => {
        if (items === null) {
            return items;
        }

        return items.filter(item => {
            if (item.dataSource === WorklistItemDataSourceType.UnlinkedOrder) {
                if (items.some(i => i.dataSource === WorklistItemDataSourceType.Order && i.accessionNumber === item.accessionNumber)) {
                    return false;
                }
            }

            return true;
        });
    };

    deleteWorklistItemByDeleteEventStream = (items: IWorklistItemDto[]): Observable<IWorklistItemDto[]> => {
        if (items?.length >= 0) {
            return this._deleteWorklistItemTrigger.pipe(
                startWith(null),
                map(deletedItemId => {
                    if (deletedItemId) {
                        const indexOfUpdatedItem = items.findIndex(
                            item => item.id.toLocaleLowerCase() === deletedItemId.toLocaleLowerCase()
                        );
                        if (indexOfUpdatedItem >= 0) {
                            items.splice(indexOfUpdatedItem, 1);
                        }
                    }

                    return items;
                })
            );
        }
        return of(items);
    };

    updateWorklistByUpsertEventStream = ([items, _]): Observable<IWorklistItemDto[]> => {
        if (items?.length >= 0) {
            return this._upsertWorklistItemTrigger.pipe(
                startWith(null),
                map(upsertedItem => {
                    if (upsertedItem && upsertedItem.orgCode === this.orgSiteSelectionService.getOrgSelectedValue().orgCode) {
                        const indexOfUpdatedItem = items.findIndex(
                            item => item.id.toLocaleLowerCase() === upsertedItem.id.toLocaleLowerCase()
                        );
                        if (indexOfUpdatedItem >= 0) {
                            items[indexOfUpdatedItem] = upsertedItem;
                        } else {
                            items.push(upsertedItem);
                        }
                    }
                    return items;
                })
            );
        }
        return of(items);
    };

    replaceAutomaticallyUpdatedItems = (items: IWorklistItemDto[]): Observable<IWorklistItemDto[]> =>
        this._updatedItemsMap.pipe(
            map(updatedItems => {
                if (updatedItems?.size > 0 && items?.length > 0) {
                    return items.map(item => (updatedItems.has(item.accessionNumber) ? updatedItems.get(item.accessionNumber) : item));
                } else {
                    return items;
                }
            })
        );

    filterWorklistByKeywords = ([items, keyword, _]): IWorklistItemDto[] => {
        if (items?.length > 0 && keyword) {
            keyword = keyword.toLowerCase();
            const itemsToReturn = items.filter(
                item =>
                    item.patientName?.toLowerCase().includes(keyword) ||
                    Constants.UrgencyDescriptions.get(item.urgency)?.toLowerCase().includes(keyword) ||
                    Constants.StatusDescriptions.get(item.status).toLowerCase().includes(keyword) ||
                    item.mrn?.toLowerCase().includes(keyword) ||
                    item.studyDescription?.toLowerCase().includes(keyword) ||
                    item.referringDoctor?.toLowerCase().includes(keyword)
            );
            if (
                this._selectedItemSource.value &&
                !itemsToReturn.find(item => item.accessionNumber === this._selectedItemSource.value.accessionNumber)
            ) {
                itemsToReturn.unshift(this._selectedItemSource.value);
            }
            return itemsToReturn;
        }
        return items;
    };

    ngOnInit(): void {
        const orgSelected$ = this.orgSiteSelectionService.getOrgSelected$();
        const hasOnlyOneSite$ = this.orgSiteSelectionService.hasOnlyOneSite();
        const sitesSelected$ = this.orgSiteSelectionService.getSitesSelected();
        const userSite$ = combineLatest([orgSelected$, sitesSelected$, hasOnlyOneSite$]).pipe(takeUntil(this.destroy$));

        const worklistItemsSource$ = userSite$
            .pipe(
                switchMap(([org, siteIds, hasOnlyOneSite]) => {
                    if (!org) {
                        return of([]);
                    }

                    const { allowedSites, orgWide } = org.getAllowedSites();

                    // If they do not have org wide permissions, we should instead only request the sites
                    // they are authorised for (rather than 'all' represented by null)
                    if (!orgWide && siteIds.includes(null)) {
                        siteIds = allowedSites;
                    }

                    // Querying with null includes all sites, including those without an id but for the appropriate org.
                    // We want to include sites without an id if the user is querying for all sites, or the org has only one site.
                    // Making the assumption here that selecting more than one site id = 'all sites'.
                    if (orgWide && (siteIds.length > 1 || hasOnlyOneSite)) {
                        siteIds = [null];
                    }

                    this._selectedItemSource.next(null); // Clear selection

                    const filtersAndTimer$ = combineLatest([
                        timer(0, Constants.PeriodicReloadTimeout).pipe(takeUntil(this.destroy$)), // Every 15 minutes
                        this.store
                            .select(WorklistFiltersState.getFilteredModalities)
                            .pipe(
                                map((modalityTypes: string[]) =>
                                    modalityTypes.map(modality => (modality === 'PlainFilm' ? ['CR', 'DX'] : modality)).flat()
                                )
                            ),
                        this.store.select(WorklistFiltersState.getFilteredUrgencies),
                        this.store.select(WorklistFiltersState.getFilteredStatuses),
                        this.store.select(WorklistFiltersState.getTimeRangeFilter)
                    ]);

                    const periodicReloadingWorklistFromRemote$ = filtersAndTimer$.pipe(
                        switchMap(([_, filteredModalityTypes, filteredUrgencyTypes, filteredStatusTypes, filterTimeRange]) => {
                            this.loading = true;
                            this._onHoldRecentlyTimers.forEach(onHoldTimer => onHoldTimer.unsubscribe);
                            this._onHoldRecentlyTimers.clear();
                            return this.worklistService
                                .query(
                                    org.orgCode,
                                    siteIds,
                                    filterTimeRange.start,
                                    filterTimeRange.end,
                                    filteredStatusTypes,
                                    filteredUrgencyTypes,
                                    true,
                                    false,
                                    filteredModalityTypes
                                )
                                .pipe(
                                    finalize(() => {
                                        this._updatedItemsMap.next(null);
                                        this.loading = false;
                                    })
                                );
                        })
                    );

                    return concat(of(null as IWorklistItemDto[]), periodicReloadingWorklistFromRemote$);
                })
            )
            .pipe(shareReplay());

        // Apply any business logic filtering and make available for subscription
        this.worklistItems$ = combineLatest([worklistItemsSource$, this._filterRefreshTrigger]).pipe(
            switchMap(this.updateWorklistByUpsertEventStream),
            switchMap(this.deleteWorklistItemByDeleteEventStream),
            switchMap(this.replaceAutomaticallyUpdatedItems),
            map(this.filterCancelledOrders),
            switchMap(this.filterWorklistItemsByTimeRange),
            switchMap(this.filterWorklistItemsByUrgencyStatusAndModalityType),
            map(this.filterMergedUnlinkedOrder),
            shareReplay()
        );

        // Filter by keyword and keep keyword when updating.
        this.worklistItems$ = combineLatest([
            this.worklistItems$,
            this.filteredKeywordsSource.pipe(startWith(null)),
            this._filterRefreshTrigger
        ]).pipe(
            map(this.filterWorklistByKeywords),
            shareReplay(),
            distinctUntilChanged((prev, current) => equal(prev, current))
        );

        // Selection of row
        this.selectedItem$ = combineLatest([this.worklistItems$, this._selectedItemSource]).pipe(
            map(([items, selectedItem]) => {
                if (!items || !selectedItem) {
                    return null;
                }

                if (items.find(item => item.id === selectedItem.id)) {
                    return selectedItem;
                } else {
                    const matchedOrder = items.find(item => item.accessionNumber === selectedItem.accessionNumber);
                    if (matchedOrder) {
                        const title = 'The worklist item has been updated';
                        const message = 'Please verify the details of the worklist item.';

                        this.messageBoxService.showMessageBox({
                            title,
                            message,
                            information: null,
                            icon: MessageBoxIcon.Information,
                            buttons: MessageBoxButtons.Ok
                        });
                        this._selectedItemSource.next(matchedOrder);
                        return matchedOrder;
                    }
                    this._selectedItemSource.next(null); // Clear selection
                    return null;
                }
            }),
            shareReplay()
        );

        // Hub Connection
        this.subscriptions.add(
            userSite$ // Whenever sites$ changes
                .pipe(
                    tap(() => {
                        // Dispose old connection
                        if (this.hub) {
                            return from(this.hub.stop());
                        }
                    })
                )
                .pipe(
                    switchMap(
                        ([org, siteIds]) =>
                            // Establish a new hub connection
                            from(
                                this.worklistService.startSignalRConnection(
                                    org.orgCode,
                                    siteIds,
                                    this.onUpsertWorklistItem,
                                    this.onDeleteWorklistItem,
                                    this.onMoveWorklistItem
                                )
                            ) // Convert Promise into Observable
                    )
                )
                .subscribe(hub => {
                    this.hub = hub; // Subscribe new HubConnection here.
                })
        );

        // Default Country Code
        const countrySettings$ = this.settingsService.getCountrySettings();
        const client$ = orgSelected$.pipe(switchMap(org => this.settingsService.getClientByOrg(org.orgCode)));

        this.defaultCountryCode$ = combineLatest([client$, countrySettings$]).pipe(
            map(([client, countrySettings]) => countrySettings.filter(c => c.country === client.countryCode)[0]?.countryCode)
        );

        this.subscriptions.add(
            this.worklistItems$.subscribe(worklistItems => {
                worklistItems?.forEach(worklistItem => {
                    // Add 5 seconds as fetching on the exact second it changes status
                    // doesn't always work
                    const tenMinutesAgo = DateTime.now().minus({ minutes: 10, seconds: 5 });
                    const accessionNumber = worklistItem.accessionNumber;

                    if (this._onHoldRecentlyTimers.has(accessionNumber)) {
                        if (worklistItem.onHold === false || this.isOnHoldStatus(worklistItem.status)) {
                            this._onHoldRecentlyTimers.get(accessionNumber).unsubscribe();
                            this._onHoldRecentlyTimers.delete(accessionNumber);
                        }
                    } else if (
                        worklistItem.onHold === true &&
                        !!worklistItem.onHoldUpdatedAt &&
                        DateTime.fromISO(worklistItem.onHoldUpdatedAt) > tenMinutesAgo
                    ) {
                        const onHoldUpdatedAt = DateTime.fromISO(worklistItem.onHoldUpdatedAt);
                        const checkForUpdateTime = tenMinutesAgo.until(onHoldUpdatedAt);
                        this._onHoldRecentlyTimers.set(
                            accessionNumber,
                            timer(checkForUpdateTime.toDuration().milliseconds)
                                .pipe(
                                    takeUntil(this.destroy$),
                                    map(_ => worklistItem)
                                )
                                .subscribe(capturedWorklistItem => this.refreshWorklistItem(capturedWorklistItem))
                        );
                    }
                });
            })
        );

        this.isOrderFormEnabled$ = client$.pipe(map(org => this.featuresService.isFeatureEnabled('orderForm', org.countryCode)));
    }

    createNewOrder(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.width = '1195px';
        dialogConfig.maxHeight = '90vh';
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.panelClass = 'create-new-order-dialog';

        const dialogRef = this.dialog.open(OrderComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // refresh worklist
            }
        });
    }

    async ngOnDestroy(): Promise<void> {
        this.subscriptions.unsubscribe();
        this._onHoldRecentlyTimers?.forEach(onHoldTimer => onHoldTimer?.unsubscribe());
        this.destroy$.next(true);
        this.destroy$.complete();
        if (this.hub) {
            await this.hub.stop();
        }
    }

    onUpsertWorklistItem = (updatedItem: IWorklistItemDto) => {
        if (this._selectedItemSource.value?.id.toLocaleLowerCase() === updatedItem.id.toLocaleLowerCase()) {
            this._selectedItemSource.next(updatedItem);
        }
        const selectedOrg = this.orgSiteSelectionService.getOrgSelectedValue();
        const selectedSites = this.orgSiteSelectionService.getSelectedSitesValue();
        const showOrdersFromAllSites = selectedSites.length === 0 || selectedSites.includes(null);
        if (updatedItem.orgCode === selectedOrg.orgCode && (showOrdersFromAllSites || selectedSites.includes(updatedItem.siteId))) {
            // only add to Worklist if the item should be visible to user in terms of orgCode and siteId
            this._upsertWorklistItemTrigger.next(updatedItem);
        }
    };

    onDeleteWorklistItem = async (id: string) => {
        if (this._selectedItemSource.value?.id.toLocaleLowerCase() === id.toLocaleLowerCase()) {
            const title = 'The worklist item has been deleted';
            const message = 'The details pane will close automatically.';

            // We await the last value from showMessageBox, because the observable afterClosed() emits two values ('next' & 'complete')
            // We don't want to proceed until we receive the last value.
            await lastValueFrom(
                this.messageBoxService.showMessageBox({
                    title,
                    message,
                    information: null,
                    icon: MessageBoxIcon.Information,
                    buttons: MessageBoxButtons.Ok
                })
            );
            this._selectedItemSource.next(null);
        }

        this._deleteWorklistItemTrigger.next(id);
    };

    onMoveWorklistItem = async (id: string, orgCode: string, siteId?: number) => {
        const selectedOrg = this.orgSiteSelectionService.getOrgSelectedValue();
        const selectedSites = this.orgSiteSelectionService.getSelectedSitesValue();
        const showOrdersFromAllSites = selectedSites.length === 0 || selectedSites.includes(null);
        if (orgCode !== selectedOrg.orgCode || !(selectedSites.includes(siteId) || showOrdersFromAllSites)) {
            // if either the new orgCode or siteId is not the same as current org/site selection, remove it from Worklist
            if (this._selectedItemSource.value?.id.toLocaleLowerCase() === id.toLocaleLowerCase()) {
                const title = 'This order has been updated';
                const message = 'You may no longer be able to view this order, and you will now be returned to the orders worklist.';

                await lastValueFrom(
                    this.messageBoxService.showMessageBox({
                        title,
                        message,
                        information: null,
                        icon: MessageBoxIcon.Information,
                        buttons: MessageBoxButtons.Ok
                    })
                );
                this._selectedItemSource.next(null);
            }

            this._deleteWorklistItemTrigger.next(id);
        }
    };

    onUpdatingKeywords = (keyword: string) => {
        this.filteredKeywordsSource.next(keyword);
    };

    onVisualRowSizeChanged(visualRowSize: number) {
        this.worklistService.ordersCount$.next(visualRowSize);
    }

    openDetails(item: IWorklistItemDto) {
        this._selectedItemSource.next(item);
        this._filterRefreshTrigger.next(null);
    }

    closeDetails() {
        this._selectedItemSource.next(null);
        this._filterRefreshTrigger.next(null);
    }

    filterTimeRangeChange = dispatch(WorklistFilterActions.UpdateTimeRangeFilter);

    openFilters() {
        this.filtersVisibleSource.next(true);
    }

    closeFilters() {
        this.filtersVisibleSource.next(false);
    }

    clearFilters() {
        this.store.dispatch(WorklistFilterActions.ClearFilters);
    }

    isOnHoldStatus(status?: string): boolean {
        return (
            status === StatusType.OnHoldMissingClinicalHistory ||
            status === StatusType.OnHoldMissingImages ||
            status === StatusType.OnHoldMissingDocumentation ||
            status === StatusType.OnHoldMissingPriors ||
            status === StatusType.OnHoldReviewOrderDetails ||
            status === StatusType.OnHoldMissingPriorImages
        );
    }

    refreshWorklistItem(worklistItem: IWorklistItemDto) {
        const accessionNumber = worklistItem.accessionNumber;
        this._onHoldRecentlyTimers.get(accessionNumber).unsubscribe();
        this._onHoldRecentlyTimers.delete(accessionNumber);

        this.subscriptions.add(
            this.worklistService
                .get(accessionNumber, WorklistItemDataSourceType.Order)
                .pipe(take(1))
                .subscribe(updatedItem => {
                    if (updatedItem !== null) {
                        const updatedItems = this._updatedItemsMap.value ?? new Map<string, IWorklistItemDto>();
                        updatedItems.set(accessionNumber, updatedItem);
                        this._updatedItemsMap.next(updatedItems);
                    }
                })
        );
    }
}
